import { DITypes, IDefaultHttpFinancasResponse } from "@/core/@types/types";
import { inject, injectable } from "inversify";
import {
  CadastroDeIntegracaoGateway,
  TIntegracaoEndpointOpcao,
  TObterIntegracaoPorIdResponse,
  TObterWebserviceParaEdicaoResponse,
  TObterWebserviceResponse
} from "./CadastroDeIntegracaoGateway";
import { type HttpClient } from "@/core/services/protocols/types";
import {
  FinancasDevHttpDi,
  type IFinancasDevHttp
} from "@/core/services/FinancasDevHttp";
import { mapLegacyValidationError } from "@/core/services/LegacyValidationError";
import { EIntegracoesAPIEnum } from "@/features/financas/views/Integracoes/@types/IntegracoesAPIEnum";

const WEB_SERVICE_API_ACTIONS = new Set([
  "Save",
  "Edit",
  "SaveNFeService",
  "SavePDVLegalService",
  "ConsultarStatusC6Pay",
  "ObterLojasGestaoClick",
  "RedeLiberarAcessoArquivos",
  "RedeVerificarStatusSolicitacao",
  "RedeCancelarAcesso"
]);

const getWebserviceApiEndpoint = (endpoint: string): string | undefined => {
  const queryIndex = endpoint.indexOf("?");
  const path = queryIndex === -1 ? endpoint : endpoint.slice(0, queryIndex);
  const query = queryIndex === -1 ? "" : endpoint.slice(queryIndex);
  const match = /^\/?(?:v1\/)?WebserviceAPI\/([^/]+)\/?$/.exec(path);

  if (!match || !WEB_SERVICE_API_ACTIONS.has(match[1])) return undefined;

  return `/v1/WebserviceAPI/${match[1]}${query}`;
};

const normalizeWebserviceApiPayload = (
  endpoint: string,
  payload: Record<string, unknown>
): unknown => {
  const action = endpoint.split("?", 1)[0].split("/").pop();

  if (
    action === "RedeVerificarStatusSolicitacao" ||
    action === "RedeCancelarAcesso"
  ) {
    return (
      payload.serviceId ??
      payload.ServiceId ??
      payload.webserviceId ??
      payload.WebserviceId ??
      payload.id ??
      payload.Id ??
      payload
    );
  }

  return payload;
};

const requiresJsonScalarBody = (endpoint: string, payload: unknown): boolean => {
  if (typeof payload !== "string") return false;

  const action = endpoint.split("?", 1)[0].split("/").pop();

  return (
    action === "RedeVerificarStatusSolicitacao" ||
    action === "RedeCancelarAcesso"
  );
};

@injectable()
export default class CadastroDeIntegracaoGatewayAdapter implements CadastroDeIntegracaoGateway {
  @inject(DITypes.FINANCAS_HTTP_CLIENT) private httpClient!: HttpClient;
  @inject(DITypes.FINANCAS_API_HTTP_CLIENT)
  private httpClientFinancasApi!: HttpClient;
  @inject(DITypes.INDICADORES_HTTP_CLIENT)
  private httpClientIndicadores!: HttpClient;

  @inject(FinancasDevHttpDi) private financasDevHttp!: IFinancasDevHttp;

  private unsupportedActionMethod(
    metodo: string
  ): IDefaultHttpFinancasResponse<unknown> {
    return { Ok: false, Error: `Metodo de acao nao suportado: ${metodo}` };
  }

  private normalizeFinancasApiBody(
    body: IDefaultHttpFinancasResponse<unknown> | undefined
  ): IDefaultHttpFinancasResponse<unknown> | undefined {
    if (!body || body.Ok !== undefined || typeof body.code !== "number")
      return body;

    const ok = body.code >= 200 && body.code < 300;

    return {
      ...body,
      Ok: ok,
      Result: body.Result ?? body.result,
      Error: !ok ? this.getErrorMessage(body) : body.Error
    };
  }

  private getErrorMessage(
    body: IDefaultHttpFinancasResponse<unknown>
  ): string | undefined {
    if (typeof body.Error === "string" && body.Error) return body.Error;
    if (typeof body.result === "string" && body.result) return body.result;

    const errorContainer = body as { Errors?: unknown; errors?: unknown };
    const errors = errorContainer.Errors ?? errorContainer.errors;

    if (typeof errors === "string" && errors) return errors;

    if (!Array.isArray(errors)) return undefined;

    const firstMessage = errors.find((error) => {
      if (typeof error === "string") return error.length > 0;
      if (!error || typeof error !== "object") return false;

      const record = error as Record<string, unknown>;

      return (
        (typeof record.message === "string" && record.message.length > 0) ||
        (typeof record.Message === "string" && record.Message.length > 0)
      );
    });

    if (typeof firstMessage === "string") return firstMessage;

    if (!firstMessage || typeof firstMessage !== "object") return undefined;

    const record = firstMessage as Record<string, unknown>;

    return typeof record.message === "string"
      ? record.message
      : typeof record.Message === "string"
        ? record.Message
        : undefined;
  }

  private async executeHttpClientAction(
    client: HttpClient,
    method: string,
    endpoint: string,
    payload: unknown,
    metodo: string
  ): Promise<IDefaultHttpFinancasResponse<unknown> | undefined> {
    if (method === "POST") {
      const jsonScalarBody = requiresJsonScalarBody(endpoint, payload);
      const response = await client.post<IDefaultHttpFinancasResponse<unknown>>(
        {
          url: endpoint,
          body: jsonScalarBody ? JSON.stringify(payload) : payload,
          config: jsonScalarBody
            ? { headers: { "Content-Type": "application/json" } }
            : undefined
        }
      );

      return this.normalizeFinancasApiBody(response.body);
    }

    if (method === "GET") {
      const response = await client.get<IDefaultHttpFinancasResponse<unknown>>({
        url: endpoint,
        queryParams:
          payload && typeof payload === "object"
            ? (payload as Record<string, unknown>)
            : undefined
      });

      return this.normalizeFinancasApiBody(response.body);
    }

    return this.unsupportedActionMethod(metodo);
  }

  private executeDevAction(
    method: string,
    endpoint: string,
    payload: unknown,
    metodo: string
  ):
    | Promise<IDefaultHttpFinancasResponse<unknown> | undefined>
    | IDefaultHttpFinancasResponse<unknown> {
    const url = `/api${endpoint}`;

    if (method === "POST") {
      const jsonScalarBody = requiresJsonScalarBody(endpoint, payload);

      return this.financasDevHttp.post(
        url,
        jsonScalarBody ? JSON.stringify(payload) : payload,
        jsonScalarBody
          ? { headers: { "Content-Type": "application/json" } }
          : undefined
      );
    }
    if (method === "GET")
      return this.financasDevHttp.get(
        url,
        payload && typeof payload === "object"
          ? (payload as Record<string, unknown>)
          : undefined
      );

    return this.unsupportedActionMethod(metodo);
  }

  private normalizeEndpointOptions(
    response: unknown
  ): TIntegracaoEndpointOpcao[] {
    const reservedKeys = new Set([
      "_id",
      "Id",
      "id",
      "valor",
      "value",
      "Nome",
      "Value",
      "Texto",
      "texto",
      "label"
    ]);

    const body = response as {
      Data?: unknown;
      Result?: unknown;
      result?: unknown;
    };
    const source = body?.Data ?? body?.Result ?? body?.result ?? response;
    const items = Array.isArray(source) ? source : [];

    return items
      .map<TIntegracaoEndpointOpcao | null>((item) => {
        if (!item || typeof item !== "object") return null;

        const record = item as Record<string, unknown>;
        const value =
          record._id ?? record.Id ?? record.id ?? record.valor ?? record.value;
        const label =
          record.Nome ??
          record.Value ??
          record.value ??
          record.Texto ??
          record.texto ??
          record.label;

        if (value == null || label == null) return null;

        const attributes = Object.entries(record).reduce<
          Record<string, unknown>
        >((acc, [key, entryValue]) => {
          if (reservedKeys.has(key)) return acc;

          acc[key] = entryValue;

          return acc;
        }, {});

        return {
          value: String(value),
          label: String(label),
          attributes: Object.keys(attributes).length ? attributes : null
        };
      })
      .filter((option): option is TIntegracaoEndpointOpcao => option !== null);
  }

  async obterOpcoesPorEndpoint(
    endpoint: string
  ): Promise<
    IDefaultHttpFinancasResponse<TIntegracaoEndpointOpcao[]> | undefined
  > {
    const financasApiEndpoint = getWebserviceApiEndpoint(endpoint);
    const resolvedEndpoint = financasApiEndpoint ?? endpoint;
    const url = import.meta.env.DEV
      ? `/api${resolvedEndpoint}`
      : resolvedEndpoint;

    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(url);

      if (response.Ok === false)
        return {
          Result: undefined,
          Error: response.Error,
          Ok: false
        };

      return {
        Result: this.normalizeEndpointOptions(response),
        Error: response.Error,
        Ok: true
      };
    }

    const client = financasApiEndpoint
      ? this.httpClientFinancasApi
      : this.httpClient;
    const response = await client.get<unknown>({
      url
    });

    return {
      Result: this.normalizeEndpointOptions(response.body),
      Error: (response.body as { Error?: string } | undefined)?.Error,
      Ok: true
    };
  }

  async getWebservice(
    id: string
  ): Promise<
    IDefaultHttpFinancasResponse<TObterWebserviceResponse> | undefined
  > {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/WebserviceAPI/Get`,
        { id }
      );

      if (!response.Ok)
        return {
          Result: undefined,
          Error: response.Error,
          Ok: false
        };

      return {
        Result: response.Result,
        Error: response.Error,
        Ok: true
      };
    }

    const response = await this.httpClientFinancasApi.get<
      IDefaultHttpFinancasResponse<TObterWebserviceResponse>
    >({
      url: `/v1/WebserviceAPI/Get`,
      queryParams: { id }
    });

    if (!response.body?.Ok)
      return {
        Result: response.body?.Result || undefined,
        Error: response.body?.Error,
        Ok: false
      };

    return {
      Result: response.body?.Result || undefined,
      Error: response.body?.Error,
      Ok: true
    };
  }

  async getWebserviceForEdit(
    id: string
  ): Promise<
    IDefaultHttpFinancasResponse<TObterWebserviceParaEdicaoResponse> | undefined
  > {
    if (!id) return undefined;

    const response = await this.httpClientIndicadores.get<
      IDefaultHttpFinancasResponse<TObterWebserviceParaEdicaoResponse>
    >({
      url: `/financas/integracoes/v1/webservice/${encodeURIComponent(id)}`
    });

    const body = this.normalizeFinancasApiBody(response.body) as
      | IDefaultHttpFinancasResponse<TObterWebserviceParaEdicaoResponse>
      | undefined;
    const result = body?.Result ?? body?.result;

    if (!body?.Ok)
      return {
        Result: result,
        Error: body?.Error,
        Ok: false
      };

    return {
      Result: result,
      Error: body?.Error,
      Ok: true
    };
  }

  async executarAcaoCampo(
    metodo: string,
    endpoint: string,
    payload: Record<string, unknown>,
    host?: string | null
  ): Promise<IDefaultHttpFinancasResponse<unknown> | undefined> {
    const method = metodo.toUpperCase();
    const consentimentoStoneEndpoint =
      "/financas/integracoes/v1/webservice/enviar-consentimento-stone";
    const isNewHorizonsEndpoint =
      host === "financas" || endpoint === consentimentoStoneEndpoint;

    const financasApiEndpoint = getWebserviceApiEndpoint(endpoint);

    if (financasApiEndpoint) {
      const requestPayload = normalizeWebserviceApiPayload(
        financasApiEndpoint,
        payload
      );

      if (import.meta.env.DEV)
        return this.executeDevAction(
          method,
          financasApiEndpoint,
          requestPayload,
          metodo
        );

      try {
        return await this.executeHttpClientAction(
          this.httpClientFinancasApi,
          method,
          financasApiEndpoint,
          requestPayload,
          metodo
        );
      } catch (error) {
        const legacyError = mapLegacyValidationError(error);
        if (legacyError) return legacyError;

        throw error;
      }
    }

    if (isNewHorizonsEndpoint) {
      try {
        return await this.executeHttpClientAction(
          this.httpClientIndicadores,
          method,
          endpoint,
          payload,
          metodo
        );
      } catch (error) {
        const body = (
          error as {
            response?: { data?: IDefaultHttpFinancasResponse<unknown> };
          }
        )?.response?.data;

        const normalized = this.normalizeFinancasApiBody(body);
        if (normalized) return normalized;

        throw error;
      }
    }

    if (import.meta.env.DEV) {
      return this.executeDevAction(method, endpoint, payload, metodo);
    }

    try {
      return await this.executeHttpClientAction(
        this.httpClient,
        method,
        endpoint,
        payload,
        metodo
      );
    } catch (error) {
      const legacyError = mapLegacyValidationError(error);
      if (legacyError) return legacyError;

      throw error;
    }
  }

  async saveWebservice(
    payload: Record<string, unknown>
  ): Promise<IDefaultHttpFinancasResponse<unknown> | undefined> {
    return this.executarAcaoCampo("POST", "/v1/WebserviceAPI/Save", payload);
  }

  async executarAcaoMultipart(
    metodo: string,
    endpoint: string,
    payload: FormData,
    host?: string | null
  ): Promise<IDefaultHttpFinancasResponse<unknown> | undefined> {
    const method = metodo.toUpperCase();
    if (method !== "POST") return this.unsupportedActionMethod(metodo);

    const financasApiEndpoint = getWebserviceApiEndpoint(endpoint);

    if (financasApiEndpoint) {
      if (import.meta.env.DEV)
        return this.financasDevHttp.post(
          `/api${financasApiEndpoint}`,
          payload
        );

      try {
        const response = await this.httpClientFinancasApi.post<
          IDefaultHttpFinancasResponse<unknown>
        >({ url: financasApiEndpoint, body: payload });

        return this.normalizeFinancasApiBody(response.body);
      } catch (error) {
        const legacyError = mapLegacyValidationError(error);
        if (legacyError) return legacyError;

        throw error;
      }
    }

    if (
      host === "financas" ||
      endpoint === "/financas/integracoes/v1/webservice/enviar-consentimento-stone"
    ) {
      const response = await this.httpClientIndicadores.post<
        IDefaultHttpFinancasResponse<unknown>
      >({ url: endpoint, body: payload });

      return this.normalizeFinancasApiBody(response.body);
    }

    if (import.meta.env.DEV)
      return this.financasDevHttp.post(`/api${endpoint}`, payload);

    try {
      const response = await this.httpClient.post<
        IDefaultHttpFinancasResponse<unknown>
      >({ url: endpoint, body: payload });

      return response.body;
    } catch (error) {
      const legacyError = mapLegacyValidationError(error);
      if (legacyError) return legacyError;

      throw error;
    }
  }

  async editWebservice(
    payload: Record<string, unknown>
  ): Promise<IDefaultHttpFinancasResponse<unknown> | undefined> {
    return this.executarAcaoCampo("POST", "/v1/WebserviceAPI/Edit", payload);
  }

  async obterEmpresasParaNexaas(): Promise<
    IDefaultHttpFinancasResponse<any[]> | undefined
  > {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/WebserviceAPI/ObterEmpresasPdvNexaasEstoka`
      );
      const result = response.Data ?? response.Result;

      if (response.Ok === false || result == null)
        return {
          Result: undefined,
          Error: response.Error,
          Ok: false
        };

      return {
        Result: result,
        Error: response.Error,
        Ok: true
      };
    }

    const response = await this.httpClientFinancasApi.get<
      IDefaultHttpFinancasResponse<any[]>
    >({
      url: `/v1/WebserviceAPI/ObterEmpresasPdvNexaasEstoka`
    });

    if (response.statusCode !== 200 || response.body?.Ok === false)
      return {
        Result:
          response.body?.Data ??
          response.body?.Result ??
          response.body?.result ??
          undefined,
        Error: response.body?.Error,
        Ok: false
      };

    return {
      Result:
        response.body?.Data ??
        response.body?.Result ??
        response.body?.result ??
        undefined,
      Error: response.body?.Error,
      Ok: true
    };
  }

  async obterIntegracaoPorId(
    apiHash: EIntegracoesAPIEnum,
    queryParams?: Record<string, string>
  ): Promise<
    IDefaultHttpFinancasResponse<TObterIntegracaoPorIdResponse> | undefined
  > {
    if (!apiHash) return undefined;

    const urlSearchParams = new URLSearchParams({
      api: String(apiHash),
      ...(queryParams ?? {})
    });

    const response = await this.httpClientIndicadores.get<
      IDefaultHttpFinancasResponse<TObterIntegracaoPorIdResponse>
    >({
      url: `/financas/integracoes/v1/definicao-campo-webservice?${urlSearchParams.toString()}`
    });

    if (!response.body?.Ok)
      return {
        Result: response.body?.result || undefined,
        Error: response.body?.Error,
        Ok: false
      };

    return {
      Result: response.body?.result || undefined,
      Error: response.body?.Error,
      Ok: true
    };
  }

  async obterMunicipiosOpt(): Promise<
    IDefaultHttpFinancasResponse<any[]> | undefined
  > {
    if (import.meta.env.DEV) {
      const response = await this.financasDevHttp.get(
        `/api/v1/WebserviceAPI/ObterCidadesAtendidasTecnospeed/`
      );
      const result = Array.isArray(response)
        ? response
        : (response.Result ?? response.Data);

      if (response.Ok === false || result == null)
        return {
          Result: undefined,
          Error: response.Error,
          Ok: false
        };

      return {
        Result: result,
        Error: response.Error,
        Ok: true
      };
    }

    const response = await this.httpClientFinancasApi.get<
      IDefaultHttpFinancasResponse<any[]>
    >({
      url: `/v1/WebserviceAPI/ObterCidadesAtendidasTecnospeed/`
    });
    const result = Array.isArray(response.body)
      ? response.body
      : (response.body?.Result ??
        response.body?.result ??
        response.body?.Data);

    if (response.statusCode !== 200 || response.body?.Ok === false)
      return {
        Result: result,
        Error: response.body?.Error,
        Ok: false
      };

    return {
      Result: result,
      Error: response.body?.Error,
      Ok: true
    };
  }
}
